import {
  getCountriesList,
  getStatesList,
  fetchCountries,
  fetchStates,
} from "@stjeans/shared-frontend/countryStateData.js"
import { AsYouType } from 'libphonenumber-js'
import randomNumber from 'random-number'

export function getLocation(locationId, locations) {
  return locations.find(x => x.id === locationId)
}

/** Box label location row — maps lodge_code to weigh_in_code for shared BoxLabels. */
export function getBoxLabelOriginalLocation(order, locations) {
  const loc = getLocation(order?.location_id, locations)
  return loc ? { ...loc, weigh_in_code: loc.lodge_code ?? "" } : null
}

/** Shared order/manifest helpers expect getOriginalLocation. */
export function getOriginalLocation(order, locations) {
  if (order?.lodge_id) {
    const loc = getLocation(order.lodge_id, locations)
    return loc ? { ...loc, weigh_in_code: loc.lodge_code ?? loc.weigh_in_code ?? "" } : null
  }
  return getBoxLabelOriginalLocation(order, locations)
}

/** Lodge box label footer line (fish tag / trip code). */
export function formatFishTag(order) {
  if (order?.fish_tag && order?.trip_code) {
    return `FISH ${order.fish_tag.toUpperCase()} / TRIP ${order.trip_code.toUpperCase()}`
  }
  if (order?.fish_tag) {
    return `FISH ${order.fish_tag.toUpperCase()}`
  }
  if (order?.trip_code) {
    return `TRIP ${order.trip_code.toUpperCase()}`
  }
  return ""
}

/** Same semantics as OMS: order’s active store / lodge row for table display. */
export function getCurrentLocation(order, locations) {
  if (order?.location_id) {
    return locations.find(x => x.id === order.location_id)
  }
  if (order?.lodge_id) {
    return locations.find(x => x.id === order.lodge_id)
  }
  return null
}

export function formatPhone(value) {
  if (!value) {
    return ""
  }
  if (!value || (value.startsWith("+") && !value.startsWith("+1"))) {
    value = new AsYouType().input(value)
  } else if (value.startsWith("+1")) {
    value = "+1 " + new AsYouType('CA').input(value.slice(2))
  } else {
    value = new AsYouType('CA').input(value)
  }

  if (value.endsWith(")")) {
    value = value.substring(0, value.length - 1)
  } else if (value.endsWith(" ")) {
    value = value.substring(0, value.length - 1)
  }
  return value
}

export function lastNameCommaFirstName(address) {
  if (!address) {
    return ""
  }

  if (address.last_name) {
    if (address.first_name) {
      return address.last_name + ", " + address.first_name
    } else {
      return address.last_name
    }
  }

  if (address.first_name) {
    return address.first_name
  }

  return ""
}

export function isOverseas(order) {
  if (order.pick_up_location_id) {
    return false // No shipping address will be set
  }
  const shipTo = shipAddress(order)
  return (shipTo?.country?.toLowerCase() !== "canada" && shipTo?.country?.toLowerCase() !== "united states")
}

export function address(order) {
  if (order.pick_up_location_id) {
    return order.bill_to
  }

  if (order.bill_same_as_ship || !order.bill_to) {
    return order.ship_to
  }

  return order.bill_to
}

export function billAddress(order) {
  return address(order)
}

export function shipAddress(order) {
  return order.ship_to
}

export function normalizedBillAddress(order) {
  let address = order.bill_to
  if (order.bill_same_as_ship || !order.bill_to) {
    return order.ship_to
  }
  return normalizedAddress(address)
}

export function normalizedShipAddress(order) {
  return normalizedAddress(order.ship_to)
}

export function normalizedAddress(address) {
  return fetchCountries()
    .then(countries => {
      address.country = normalizeCountry(address.country, countries)
      return fetchStates(countries.find(x => x.name === address.country)?.id)
    })
    .then(states => {
      address.state = normalizeState(address.state, states)
      address.phone = formatPhone(address.phone)
      return address
    })
}

export function normalizeState(state, stateList) {
  if (!state || !stateList || stateList.length === 0) {
    return ""
  }

  // This function exists because state used to be a free field
  const match = stateList.find(x => {
    if (x.name.trim().toLowerCase() === state.trim().toLowerCase()) {
      return true
    }
    if (x.state_code.trim().toLowerCase() === state.trim().toLowerCase()) {
      return true
    }
    return false
  })
  if (match) {
    return match.state_code
  }

  return ""
}

export function normalizeCountry(country, countriesList) {
  if (!country || !countriesList || countriesList.length === 0) {
    return ""
  }

  // This function exists because country used to be a free field
  const match = countriesList.find(x => {
    if (x.name.trim().toLowerCase() === country.trim().toLowerCase()) {
      return true
    }
    if (x.iso2.trim().toLowerCase() === country.trim().toLowerCase()) {
      return true
    }
    if (x.iso3.trim().toLowerCase() === country.trim().toLowerCase()) {
      return true
    }
    return false
  })
  if (match) {
    return match.name
  }

  return ""
}

export function normalizeProvince(province) {
  if (!province) {
    return null
  }
  // Temporary measure, please replace this with a library
  province = province.toLowerCase().trim()
  province.replaceAll('.', '')
  province.replaceAll('-', '')
  province.replaceAll('é', 'e')
  province.replaceAll('î', 'i')
  switch (province) {
    case "alta":
    case "alb":
    case "alberta":
    case "ab":
      return "AB"
    case "bc":
    case "cb":
    case "british columbia":
      return "BC"
    case "man":
    case "manitoba":
    case "mb":
      return "MB"
    case "nb":
    case "new brunswick":
      return "NB"
    case "nl":
    case "tnl":
    case "newfoundland":
    case "labrador":
    case "newfoundland and labrador":
      return "NL"
    case "nwt":
    case "tno":
    case "northwest territories":
    case "nt":
      return "NT"
    case "ns":
    case "ne":
    case "nova scotia":
      return "NS"
    case "nvt":
    case "nunavut":
    case "nu":
      return "NU"
    case "ont":
    case "ontario":
    case "on":
      return "ON"
    case "pei":
    case "ipe":
    case "prince edward island":
    case "pe":
      return "PE"
    case "que":
    case "qc":
    case "quebec":
      return "QC"
    case "sask":
    case "saskatchewan":
    case "sk":
      return "SK"
    case "yt":
    case "yn":
    case "yukon":
    case "the yukon":
    case "yukon territory":
    case "the yukon territory":
      return "YT"
    default:
      return null
  }
}

export function normalizeProvinceOrState(country, value, stateList) {
  if (country === "Canada") {
    return normalizeProvince(value)
  } else if (country === "United States") {
    return normalizeState(value, stateList)
  } else {
    return value
  }
}

export function customerName(order) {
  const a = address(order)
  if (!a) {
    return ""
  }
  return [a.first_name, a.last_name].join(" ")
}

export function hasBeenConfirmed(order) {
  return !(!order.status || ["", "new"].includes(order.status))
}

export function hasBeenShipped(order) {
  return !(!order.status || ["", "new", "confirmed"].includes(order.status))
}

export function hasBeenReceived(order) {
  return !(!order.status || ["", "new", "confirmed", "shipped"].includes(order.status))
}

export function hasBeenWeighedIn(order) {
  return !(!order.status || ["", "new", "confirmed", "shipped", "received"].includes(order.status))
}

export function newWeighInDetail() {
  return {
    weigh_in_process_id: null,
    species_id: null,
    weight: null,
    half_lb: null,
    x_species_code: null,
    x_description: null,
    fish_states: null
  }
}

export function orderDetailToWeighInDetail(orderDetail, orderOptions, options, weighInProcesses) {
  const weighInProcess = calculateWeighInProcess(orderDetail.process_id, orderDetail.species_id, orderOptions, options, weighInProcesses)
  return {
    ...newWeighInDetail(),
    weigh_in_process_id: weighInProcess?.id,
    half_lb: newHalfLbValue(false, weighInProcess),
    species_id: orderDetail.species_id,
    original_num_fish: orderDetail.num_fish // This field is never saved to DB, its just for convenience for the UI
  }
}

export function newHalfLbValue(previousValue, weighInProcess) {
  if (!weighInProcess) {
    return previousValue
  }
  switch (weighInProcess.half_lb_option) {
    case "never":
      return false
    case "always":
      return true
    default:
      return previousValue
  }
}

export function calculateWeighInProcess(processId, speciesId, orderOptions, options, weighInProcesses) {
  const possibleWeighInProcesses = weighInProcesses.filter(wip => wip.process_id === processId && wip.tuna_only === (speciesId === 10))
  if (possibleWeighInProcesses.length === 1) {
    return possibleWeighInProcesses[0] // Adding this condition specifically for Tuna, because the options might not match but we'll still know what it should be
  }

  const relevantOptionIds = orderOptions.filter(oo => oo.value && optionsForProcess(processId, options).find(ofp => ofp.id === oo.option_id)).map(oo => oo.option_id)
  const matchingWeighInProcesses = possibleWeighInProcesses.filter(wip => [...new Set([wip.pin_out_option_id, wip.skin_off_option_id].filter(x => x))].sort().join(',') === relevantOptionIds.sort().join(','))
  if (!matchingWeighInProcesses || matchingWeighInProcesses.length === 0) {
    return null
  }
  return matchingWeighInProcesses.sort((a, b) => a.id - b.id)[0] // There can be multiple matches so return the one with lower id
}

export function optionsForProcess(processId, options) {
  return options.filter(o => [o.process_id1, o.process_id2, o.process_id3, o.process_id4, o.process_id5].includes(processId))
}

export function calculateFishCount(weighInDetail) {
  if (!weighInDetail || !weighInDetail.fish_states) {
    return 0
  }
  let total = 0
  weighInDetail.fish_states.forEach(fs => {
    total += Number(fs.num_fish ? fs.num_fish : 0)
  })
  return total
}

export function manifestStatusColor(status) {
  if (!status) {
    status = "new"
  }

  switch (status?.toLowerCase()) {
    case "new":
      return "#d93f3f"
    case "open":
      return "#34b356"
    case "closed":
      return "#d93f3f"
    case "shipped":
      return "#3491b3"
    default:
      return "black"
  }
}

export function statusColor(status) {
  if (!status) {
    status = "new"
  }

  switch (status?.toLowerCase()) {
    case "new":
      return "#d93f3f"
    case "confirmed":
      return "#34b356"
    case "shipped":
      return "#3491b3"
    case "received":
      return "#8d34b3"
    case "weighed_in":
      return "#6ebaa4"
    default:
      return "black"
  }
}

export function formatManifestStatus(status) {
  if (!status || status.toLowerCase() === "new" || status.toLowerCase() === "open") {
    return "Open"
  }
  if (!status || status.toLowerCase() === "closed") {
    return "Closed"
  }
  if (!status || status.toLowerCase() === "shipped") {
    return "Sent to St Jeans"
  }
  return status.charAt(0).toUpperCase() + status.slice(1)
}

export function formatStatus(status) {
  if (!status || status.toLowerCase() === "new") {
    return "Unconfirmed"
  }
  if (!status || status.toLowerCase() === "shipped") {
    return "Transferring"
  }
  if (!status || status.toLowerCase() === "weighed_in") {
    return "Weighed In"
  }
  return status.charAt(0).toUpperCase() + status.slice(1)
}

export function maxDecimalPlaces(num, numDecimals) {
  return parseFloat(parseFloat(num).toFixed(numDecimals))
}

export function sleep(ms) {
  return new Promise(r => setTimeout(r, ms))
}

export function formatFileSize(bytes) {
  if (!bytes) {
    return "0 B"
  }

  if (bytes < 100) {
    return bytes + " B"
  }

  if (bytes < 100000) {
    return (Math.round(bytes / 100.0) / 10) + " kB"
  }

  if (bytes < 100000000) {
    return (Math.round(bytes / 100000.0) / 10) + " MB"
  }
  return (Math.round(bytes / 100000000.0) / 10) + " GB"
}

// Example: trimSubString(", , , test, , ", ", ") => "test"
export function trimSubString(input, substring) {
  var result = input

  if (!input || !substring) {
    return input
  }

  while (true) {
    if (result.startsWith(substring)) {
      result = result.slice(substring.length)
    } else {
      break
    }
  }

  while (true) {
    if (result.endsWith(substring)) {
      result = result.slice(0, substring.length * -1)
    } else {
      break
    }
  }

  return result
}

export function validateConfirmOrder(order) {
  const missingFields = []

  if (!order.ordered_at) {
    missingFields.push("order date")
  }

  if (!order.fishing_license) {
    missingFields.push("fishing license")
  }

  if (!order.email) {
    missingFields.push("email")
  }

  if (!order.phone) {
    missingFields.push("phone")
  }

  if (!order.pick_up_location_id) {
    if (!order.ship_to?.first_name) {
      missingFields.push("ship-to first name")
    }

    if (!order.ship_to?.last_name) {
      missingFields.push("ship-to last name")
    }

    if (!order.ship_to?.street_address) {
      missingFields.push("ship-to street address")
    }

    if (!order.ship_to?.city) {
      missingFields.push("ship-to city")
    }

    if (!order.ship_to?.state) {
      missingFields.push("ship-to state")
    }

    if (!order.ship_to?.zip) {
      missingFields.push("ship-to zip")
    }

    if (!order.ship_to?.country) {
      missingFields.push("ship-to country")
    }
  }

  if (!order.bill_same_as_ship || order.pick_up_location_id) {
    if (!order.bill_to?.first_name) {
      missingFields.push("bill-to first name")
    }

    if (!order.bill_to?.last_name) {
      missingFields.push("bill-to last name")
    }

    if (!order.bill_to?.street_address) {
      missingFields.push("bill-to stree address")
    }

    if (!order.bill_to?.city) {
      missingFields.push("bill-to city")
    }

    if (!order.bill_to?.state) {
      missingFields.push("bill-to state")
    }

    if (!order.bill_to?.zip) {
      missingFields.push("bill-to zip")
    }

    if (!order.bill_to?.country) {
      missingFields.push("bill-to country")
    }
  }

  if (!order.hold_for_pick_up) {
    // Details
    if (order.details.length === 0) {
      missingFields.push("processes")
    }

    if (!order.total_weight_lb) {
      missingFields.push("total weight")
    }

    if (!order.frozen && !order.fresh) {
      missingFields.push("fresh/frozen")
    }
  }

  if (!order.total_boxes) {
    missingFields.push("total boxes")
  }

  // TODO
  // if (!order.signature_img) {
  //     missingFields.push("signature")
  // }

  let invalid = false

  if (missingFields.length > 0) {
    invalid = true
  }

  return [invalid, missingFields, order]
}

// Input a date object
// Output like "2025-03-09"
export function formatDateDashes(date) {
  const year = date.getFullYear()
  const month = (date.getMonth() + 1).toString().padStart(2, "0")
  const day = date.getDate().toString().padStart(2, "0")
  return `${year}-${month}-${day}`
}

// Input a date object
// Output like "2025-03-09T16-57-09"
export function formatTimeDashes(date) {
  const year = date.getFullYear()
  const month = (date.getMonth() + 1).toString().padStart(2, "0")
  const day = date.getDate().toString().padStart(2, "0")
  const hour = date.getHours().toString().padStart(2, "0")
  const minute = date.getMinutes().toString().padStart(2, "0")
  const second = date.getSeconds().toString().padStart(2, "0")
  return `${year}-${month}-${day}T${hour}-${minute}-${second}`
}

export function formatContactMethod(contactMethod) {
  switch (contactMethod) {
    case "phone":
      return "Phone"
    case "text":
      return "Text"
    default:
      return "Email"
  }
}

export function serialify(obj) {
  return JSON.parse(JSON.stringify(obj))
}

export function randomId() {
  return randomNumber({ min: 1, max: 999999999999999, integer: true })
}

export async function serialifyRequest(request) {
  const requestCopy = request.clone()
  const buffer = await requestCopy.bytes()
  const bodyStr = new TextDecoder().decode(buffer)
  const newRequest = {
    body: buffer,
    body_text: bodyStr,
    headers: serialifyHeaders(requestCopy.headers),
    method: requestCopy.method,
    url: requestCopy.url
  }
  return newRequest
}

export function serialifyHeaders(headers) {
  const newHeaders = {}
  for (const pair of headers.entries()) {
    newHeaders[pair[0]] = pair[1]
  }
  return newHeaders
}

export function serialifyError(error) {
  return {
    message: error.message,
    name: error.name,
    stack: error.stack
  }
}

// Returns a structured version of an API URL. This function does require up-to-date knowledge of the API endpoints to work properly.
// 
// Input: ${API_URL}/api/manifests?page=2&per_page=25&sort_by=ordered_at&sort_direction=desc&search=M132402&location=34
// Returns:
//  {
//      resource: "manifests",
//      id: null,     // Contains ID if applicable
//      action: null, // For POST request this might be "ship", "confirm", etc
//      query: {
//          page: "2",
//          per_page: "25",
//          sort_by: "ordered_at",
//          sort_direction: "desc",
//          search: "M132402",
//          location: "34"
//      }
//  }
export function parseApiUrl(url) {
  const result = {
    url: url,
    resource: null,
    id: null,
    action: null,
    query: {}
  }

  const base = import.meta.env.VITE_API_URL
  const urlObj = new URL(url, base) // Base only used if URL is relative
  if (`${urlObj.protocol}//${urlObj.host}` !== base) {
    return null // This url isn't part of our API
  }

  const pathParts = urlObj.pathname.split("/") // 0: "", 1: "api", 2: <resource | "v2">, 3: <id | action | resource>, ...
  const apiVersionOffset = pathParts[2] === "v2" ? 1 : 0

  // Resource
  if (pathParts.length >= 3 + apiVersionOffset) {
    result.resource = pathParts[2 + apiVersionOffset]
  }

  // ID | Action
  if (pathParts.length >= 4 + apiVersionOffset) {
    const part = pathParts[3 + apiVersionOffset]
    // tripcodes use composite string ids (e.g. 2026-06A), not numeric ids or route actions
    if (
      result.resource === "tripcodes" ||
      Number(part) ||
      part.startsWith("$")
    ) {
      result.id = Number(part) || part
    } else {
      result.action = part
    }
  }

  // Action
  if (pathParts.length >= 5 + apiVersionOffset) {
    result.action = pathParts[4 + apiVersionOffset]
  }

  // Query
  result.query = Object.fromEntries(urlObj.searchParams.entries())

  return result
}

export function isCreateOrderRequest(method, parsedUrl) {
  // v2 order creation uses POST /api/orders/v2
  return method === "POST" && parsedUrl.resource === "orders" && !parsedUrl.id && parsedUrl.action === "v2"
}

export function isUpdateOrderRequest(method, parsedUrl) {
  return method === "PUT" && parsedUrl.resource === "orders" && parsedUrl.id && !parsedUrl.action
}

export function isUploadFileRequest(method, parsedUrl) {
  return method === "POST" && parsedUrl.resource === "orders" && parsedUrl.id && parsedUrl.action === "upload"
}

export function isUploadSignatureRequest(method, parsedUrl) {
  return method === "POST" && parsedUrl.resource === "orders" && parsedUrl.id && parsedUrl.action === "signature"
}

export function isUploadLicenseRequest(method, parsedUrl) {
  // v1 supported POST /orders/:id/license; v2 uses license-front + license-back
  return method === "POST" && parsedUrl.resource === "orders" && parsedUrl.id && ["license", "license-front", "license-back"].includes(parsedUrl.action)
}

export function isDeleteFileRequest(method, parsedUrl) {
  return method === "POST" && parsedUrl.resource === "orders" && parsedUrl.id && parsedUrl.action === "delete-file"
}

export function isDeleteOrderRequest(method, parsedUrl) {
  return method === "DELETE" && parsedUrl.resource === "orders" && parsedUrl.id && !parsedUrl.action
}